import { useState, useEffect, useRef, useCallback, type TouchEvent as ReactTouchEvent } from "react";
import { useTranslation } from "react-i18next";
import { useParallax } from "@/hooks/useParallax";
import ScrollReveal from "@/components/feature/ScrollReveal";

interface CentroCard {
  id: string;
  title: string;
  subtitle: string;
  longDesc: string;
  features: string[];
  icon: string;
  stat: string;
  statLabel: string;
  image: string;
  srcSet: string;
}

const centroCards: CentroCard[] = [
  {
    id: "soc-24",
    title: "CyberSOC 24/7",
    subtitle: "Centro de Operaciones de Seguridad con monitoreo continuo las 24 horas, los 7 dias de la semana, protegiendo infraestructuras criticas en tiempo real.",
    longDesc: "Nuestro Centro de Operaciones de Seguridad opera sin interrupciones los 365 dias del ano. Analistas certificados correlacionan eventos de red, endpoint, cloud e identidad en tiempo real para detectar y neutralizar amenazas antes de que impacten tu operacion.",
    features: [
      "Monitoreo y correlacion de eventos 24/7/365",
      "Analistas certificados en turnos continuos",
      "Playbooks de respuesta automatizados",
      "Escalamiento y contencion en minutos",
    ],
    icon: "ri-radar-line",
    stat: "24/7/365",
    statLabel: "Monitoreo continuo",
    image: "https://readdy.ai/api/search-image?query=Diverse%20team%20of%20security%20analysts%20collaborating%20in%20a%20modern%20security%20operations%20center%2C%20reviewing%20threat%20dashboards%20on%20large%20screens%2C%20warm%20ambient%20light%20with%20cyan%20glow%2C%20friendly%20focused%20professionals%2C%20editorial%20photography%2C%20human%20centered%2C%20cinematic%20depth%20of%20field&width=1600&height=900&seq=centro-soc-01&orientation=landscape&nocache=true",
    srcSet: "https://readdy.ai/api/search-image?query=Diverse%20team%20of%20security%20analysts%20collaborating%20in%20a%20modern%20security%20operations%20center%2C%20reviewing%20threat%20dashboards%20on%20large%20screens%2C%20warm%20ambient%20light%20with%20cyan%20glow%2C%20friendly%20focused%20professionals%2C%20editorial%20photography%2C%20human%20centered%2C%20cinematic%20depth%20of%20fieldquery=Diverse%20team%20of%20security%20analysts%20collaborating%20in%20a%20modern%20security%20operations%20center%2C%20reviewing%20threat%20dashboards%20on%20large%20screens%2C%20warm%20ambient%20light%20with%20cyan%20glow%2C%20friendly%20focused%20professionals%2C%20editorial%20photography%2C%20human%20centered%2C%20cinematic%20depth%20of%20field&width=640&height=360&seq=centro-soc-01&orientation=landscape&nocache=true%20640w%2C%20https:%2F%2Freaddy.ai%2Fapi%2Fsearch-image%3Fwidth=1280&height=720&seq=centro-soc-01&orientation=landscape&nocache=true%201280w",
  },
  {
    id: "lab-forense",
    title: "Laboratorio Forense",
    subtitle: "Analisis forense digital avanzado con herramientas de ultima generacion para investigacion de incidentes, recuperacion de evidencias y cadena de custodia.",
    longDesc: "Contamos con un laboratorio de analisis forense digital equipado con herramientas de ultima generacion para la investigacion de incidentes, recuperacion de evidencias y preservacion de la cadena de custodia, con validez pericial.",
    features: [
      "Analisis forense de endpoints y servidores",
      "Recuperacion de evidencia digital",
      "Cadena de custodia certificada",
      "Informes tecnicos con validez pericial",
    ],
    icon: "ri-microscope-line",
    stat: "ISO 27001",
    statLabel: "Certificado",
    image: "https://readdy.ai/api/search-image?query=Digital%20forensics%20specialists%20examining%20evidence%20on%20large%20screens%20in%20a%20modern%20bright%20laboratory%2C%20two%20professionals%20collaborating%20thoughtfully%2C%20warm%20natural%20light%20with%20subtle%20cyan%20accent%2C%20editorial%20photography%2C%20human%20centered%2C%20cinematic%20depth%20of%20field&width=1600&height=900&seq=centro-lab-01&orientation=landscape&nocache=true",
    srcSet: "https://readdy.ai/api/search-image?query=Digital%20forensics%20specialists%20examining%20evidence%20on%20large%20screens%20in%20a%20modern%20bright%20laboratory%2C%20two%20professionals%20collaborating%20thoughtfully%2C%20warm%20natural%20light%20with%20subtle%20cyan%20accent%2C%20editorial%20photography%2C%20human%20centered%2C%20cinematic%20depth%20of%20fieldquery=Digital%20forensics%20specialists%20examining%20evidence%20on%20large%20screens%20in%20a%20modern%20bright%20laboratory%2C%20two%20professionals%20collaborating%20thoughtfully%2C%20warm%20natural%20light%20with%20subtle%20cyan%20accent%2C%20editorial%20photography%2C%20human%20centered%2C%20cinematic%20depth%20of%20field&width=640&height=360&seq=centro-lab-01&orientation=landscape&nocache=true%20640w%2C%20https:%2F%2Freaddy.ai%2Fapi%2Fsearch-image%3Fwidth=1280&height=720&seq=centro-lab-01&orientation=landscape&nocache=true%201280w",
  },
  {
    id: "red-team",
    title: "Red Team & Pentesting",
    subtitle: "Equipo ofensivo especializado en simulaciones de ataque controladas, ethical hacking y pruebas de penetracion para identificar vulnerabilidades antes que los atacantes.",
    longDesc: "Nuestro equipo ofensivo simula ataques reales de adversarios para poner a prueba tus defensas. Identificamos vulnerabilidades explotables antes que los atacantes y te ayudamos a priorizar y remediar cada hallazgo.",
    features: [
      "Simulaciones de adversarios (Red Team)",
      "Pentesting de aplicaciones y redes",
      "Ethical hacking y analisis de vulnerabilidades",
      "Remediacion priorizada por riesgo",
    ],
    icon: "ri-shield-flash-line",
    stat: "4.5/5",
    statLabel: "Rating SOC",
    image: "https://readdy.ai/api/search-image?query=Ethical%20hacking%20team%20working%20together%20in%20a%20modern%20office%20with%20multiple%20monitors%20showing%20security%20dashboards%2C%20focused%20professionals%20collaborating%2C%20warm%20ambient%20lighting%20with%20subtle%20red%20accent%2C%20editorial%20photography%2C%20human%20centered%2C%20cinematic%20depth%20of%20field&width=1600&height=900&seq=centro-red-01&orientation=landscape&nocache=true",
    srcSet: "https://readdy.ai/api/search-image?query=Ethical%20hacking%20team%20working%20together%20in%20a%20modern%20office%20with%20multiple%20monitors%20showing%20security%20dashboards%2C%20focused%20professionals%20collaborating%2C%20warm%20ambient%20lighting%20with%20subtle%20red%20accent%2C%20editorial%20photography%2C%20human%20centered%2C%20cinematic%20depth%20of%20fieldquery=Ethical%20hacking%20team%20working%20together%20in%20a%20modern%20office%20with%20multiple%20monitors%20showing%20security%20dashboards%2C%20focused%20professionals%20collaborating%2C%20warm%20ambient%20lighting%20with%20subtle%20red%20accent%2C%20editorial%20photography%2C%20human%20centered%2C%20cinematic%20depth%20of%20field&width=640&height=360&seq=centro-red-01&orientation=landscape&nocache=true%20640w%2C%20https:%2F%2Freaddy.ai%2Fapi%2Fsearch-image%3Fwidth=1280&height=720&seq=centro-red-01&orientation=landscape&nocache=true%201280w",
  },
  {
    id: "training-center",
    title: "Centro de Entrenamiento",
    subtitle: "Instalaciones dedicadas a la formacion de profesionales en ciberseguridad con laboratorios practicos, simuladores de ataque y certificaciones oficiales.",
    longDesc: "Instalaciones disenadas para la formacion practica de profesionales en ciberseguridad: laboratorios con simuladores de ataque, entornos controlados y programas alineados a certificaciones oficiales de la industria.",
    features: [
      "Laboratorios practicos con simuladores",
      "Programas alineados a certificaciones oficiales",
      "Instructores certificados en la industria",
      "Ciberrangos para ejercicios en vivo",
    ],
    icon: "ri-graduation-cap-line",
    stat: "+200",
    statLabel: "Especialistas",
    image: "https://readdy.ai/api/search-image?query=Cybersecurity%20instructor%20teaching%20a%20small%20group%20of%20engaged%20students%20in%20a%20modern%20bright%20training%20classroom%2C%20learners%20at%20workstations%20with%20screens%2C%20warm%20natural%20light%20mixed%20with%20soft%20blue%20accent%2C%20editorial%20photography%2C%20human%20centered%2C%20cinematic%20depth%20of%20field&width=1600&height=900&seq=centro-train-01&orientation=landscape&nocache=true",
    srcSet: "https://readdy.ai/api/search-image?query=Cybersecurity%20instructor%20teaching%20a%20small%20group%20of%20engaged%20students%20in%20a%20modern%20bright%20training%20classroom%2C%20learners%20at%20workstations%20with%20screens%2C%20warm%20natural%20light%20mixed%20with%20soft%20blue%20accent%2C%20editorial%20photography%2C%20human%20centered%2C%20cinematic%20depth%20of%20fieldquery=Cybersecurity%20instructor%20teaching%20a%20small%20group%20of%20engaged%20students%20in%20a%20modern%20bright%20training%20classroom%2C%20learners%20at%20workstations%20with%20screens%2C%20warm%20natural%20light%20mixed%20with%20soft%20blue%20accent%2C%20editorial%20photography%2C%20human%20centered%2C%20cinematic%20depth%20of%20field&width=640&height=360&seq=centro-train-01&orientation=landscape&nocache=true%20640w%2C%20https:%2F%2Freaddy.ai%2Fapi%2Fsearch-image%3Fwidth=1280&height=720&seq=centro-train-01&orientation=landscape&nocache=true%201280w",
  },
  {
    id: "threat-intel",
    title: "Ciberinteligencia",
    subtitle: "Unidad de inteligencia de amenazas con monitoreo de dark web, analisis de actores maliciosos y generacion de indicadores de compromiso proactivos.",
    longDesc: "Nuestra unidad de inteligencia de amenazas monitorea la dark web, foros underground y fuentes OSINT globales para generar indicadores de compromiso proactivos y alertas tempranas adaptadas a tu industria.",
    features: [
      "Monitoreo de dark web y foros underground",
      "Indicadores de compromiso (IoC) proactivos",
      "Analisis de actores y campanas maliciosas",
      "Boletines y alertas por industria",
    ],
    icon: "ri-spy-line",
    stat: "5 Paises",
    statLabel: "Presencia",
    image: "https://readdy.ai/api/search-image?query=Threat%20intelligence%20analysts%20discussing%20global%20security%20data%20around%20a%20large%20display%20in%20a%20modern%20dark%20office%2C%20team%20collaboration%2C%20warm%20human%20atmosphere%20with%20cyan%20and%20amber%20glow%2C%20editorial%20photography%2C%20human%20centered%2C%20cinematic%20depth%20of%20field&width=1600&height=900&seq=centro-intel-01&orientation=landscape&nocache=true",
    srcSet: "https://readdy.ai/api/search-image?query=Threat%20intelligence%20analysts%20discussing%20global%20security%20data%20around%20a%20large%20display%20in%20a%20modern%20dark%20office%2C%20team%20collaboration%2C%20warm%20human%20atmosphere%20with%20cyan%20and%20amber%20glow%2C%20editorial%20photography%2C%20human%20centered%2C%20cinematic%20depth%20of%20fieldquery=Threat%20intelligence%20analysts%20discussing%20global%20security%20data%20around%20a%20large%20display%20in%20a%20modern%20dark%20office%2C%20team%20collaboration%2C%20warm%20human%20atmosphere%20with%20cyan%20and%20amber%20glow%2C%20editorial%20photography%2C%20human%20centered%2C%20cinematic%20depth%20of%20field&width=640&height=360&seq=centro-intel-01&orientation=landscape&nocache=true%20640w%2C%20https:%2F%2Freaddy.ai%2Fapi%2Fsearch-image%3Fwidth=1280&height=720&seq=centro-intel-01&orientation=landscape&nocache=true%201280w",
  },
];

interface CentroCiberseguridadProps {
  onOpenQuote?: (service?: string) => void;
}

export default function CentroCiberseguridad({ onOpenQuote }: CentroCiberseguridadProps) {
  const { t } = useTranslation("common");
  const [current, setCurrent] = useState(0);
  const [detailIndex, setDetailIndex] = useState<number | null>(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  // Solo montamos el slide visible y el anterior (para el crossfade), asi no se descargan las 5 imagenes de golpe
  const [renderedSlides, setRenderedSlides] = useState<number[]>([0]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const { ref: bgRef, offset } = useParallax(0.1);

  const total = centroCards.length;

  useEffect(() => {
    setRenderedSlides((prev) => {
      if (prev.includes(current)) return prev;
      const nextList = [...prev, current];
      return nextList.length > 2 ? nextList.slice(-2) : nextList;
    });
  }, [current]);

  const goTo = useCallback((index: number) => {
    setCurrent(((index % total) + total) % total);
  }, [total]);

  const next = useCallback(() => {
    goTo(current + 1);
  }, [current, goTo]);

  const prev = useCallback(() => {
    goTo(current - 1);
  }, [current, goTo]);

  const pauseTemporarily = useCallback(() => {
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 9000);
  }, []);

  const openDetail = useCallback((index: number) => {
    setCurrent(((index % total) + total) % total);
    setDetailIndex(((index % total) + total) % total);
    setIsAutoPlaying(false);
  }, [total]);

  const closeDetail = useCallback(() => {
    setDetailIndex(null);
    pauseTemporarily();
  }, [pauseTemporarily]);

  useEffect(() => {
    if (!isAutoPlaying || detailIndex !== null) return;
    intervalRef.current = setInterval(next, 5500);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAutoPlaying, next, detailIndex]);

  useEffect(() => {
    if (detailIndex === null) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDetail();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [detailIndex, closeDetail]);

  const handleDotClick = (index: number) => {
    goTo(index);
    pauseTemporarily();
  };

  const handleNavClick = (direction: "prev" | "next") => {
    if (direction === "prev") prev();
    else next();
    pauseTemporarily();
  };

  const handleTouchStart = (e: ReactTouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: ReactTouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - (touchStartY.current ?? 0);
    if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) next();
      else prev();
      pauseTemporarily();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const scrollTo = (target: string) => {
    const el = document.querySelector(target);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const card = centroCards[current];
  const detailCard = detailIndex !== null ? centroCards[detailIndex] : null;

  return (
    <>
      <section
        id="centro-ciberseguridad"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        className="relative w-full min-h-[560px] md:min-h-[660px] lg:min-h-[720px] overflow-hidden bg-background-950 flex items-center touch-pan-y"
      >
        {/* Parallax background slides (video-like crossfade) */}
        <div ref={bgRef} className="absolute inset-0">
          {centroCards.map((c, idx) =>
            renderedSlides.includes(idx) ? (
              <img
                key={c.id}
                src={c.image}
                srcSet={c.srcSet}
                sizes="100vw"
                alt={c.title}
                className={`absolute -top-[6%] left-0 w-full h-[112%] object-cover object-center transition-all duration-[1200ms] ease-in-out ${
                  idx === current ? "opacity-100 scale-100" : "opacity-0 scale-105"
                }`}
                decoding="async"
                style={{ transform: idx === current ? `translateY(${offset}px)` : undefined }}
              />
            ) : null,
          )}
        </div>

        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/95 via-background-950/80 to-background-950/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/25 to-background-950/70" />
        <div className="absolute top-0 left-0 right-0 h-24 md:h-28 bg-gradient-to-b from-background-950 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 h-24 md:h-28 bg-gradient-to-t from-background-950 to-transparent pointer-events-none" />

        {/* Tech grid */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(oklch(var(--primary-500) / 0.4) 1px, transparent 1px), linear-gradient(90deg, oklch(var(--primary-500) / 0.4) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />

        {/* Content */}
        <div className="relative z-10 w-full px-5 sm:px-8 lg:px-14 py-14 md:py-20">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-primary-400/50" />
                <span className="text-[10px] font-label font-semibold text-primary-300 uppercase tracking-[0.3em]">
                  {t("cs.centro.tag")}
                </span>
              </div>
              <h2 className="font-heading font-bold text-[22px] leading-[1.18] sm:text-3xl md:text-4xl lg:text-5xl sm:leading-tight text-white max-w-3xl mb-3 md:mb-4">
                {t("cs.centro.title")} <span className="text-primary-400">{t("cs.centro.highlight")}</span>
              </h2>
              <p className="text-sm md:text-base text-white/60 max-w-2xl leading-relaxed">
                {t("cs.centro.subtitle")}
              </p>
            </ScrollReveal>

            <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end">
              {/* Left: current slide copy */}
              <div className="lg:col-span-7">
                <div key={current} className="animate-fade-in-up">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary-400/25 bg-primary-500/10 mb-5">
                    <i className={`${card.icon} text-primary-300 text-sm`} />
                    <span className="text-[10px] font-label font-semibold text-primary-200 uppercase tracking-[0.2em]">
                      {String(current + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                    </span>
                  </div>

                  <h3 className="font-heading font-bold text-xl md:text-2xl lg:text-3xl text-white mb-3">
                    {card.title}
                  </h3>

                  {/* Stable min-height avoids layout jump between slides on mobile */}
                  <div className="min-h-[124px] sm:min-h-[104px] lg:min-h-[88px] mb-6 md:mb-7">
                    <p className="text-sm md:text-base text-white/70 leading-relaxed max-w-xl">
                      {card.subtitle}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-6">
                    <div>
                      <p className="font-heading font-bold text-2xl md:text-3xl text-primary-300 leading-none">{card.stat}</p>
                      <p className="text-[10px] font-label text-white/40 uppercase tracking-wider mt-1">{card.statLabel}</p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <a
                        href="#features"
                        onClick={(e) => {
                          e.preventDefault();
                          scrollTo("#features");
                        }}
                        className="group inline-flex items-center gap-3 pl-5 pr-2 py-1.5 rounded-full border border-primary-400/50 bg-primary-500/15 backdrop-blur-md text-white font-label font-medium text-sm hover:border-primary-400/80 hover:bg-primary-500/25 transition-all duration-500 whitespace-nowrap cursor-pointer"
                      >
                        <span>{t("shared.ctaPrimary")}</span>
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white group-hover:bg-primary-400 transition-colors duration-300">
                          <i className="ri-arrow-right-line text-sm group-hover:translate-x-0.5 transition-transform duration-300" />
                        </span>
                      </a>
                      <button
                        type="button"
                        onClick={() => onOpenQuote?.("CyberSOC")}
                        className="group inline-flex items-center gap-3 pl-5 pr-2 py-1.5 rounded-full border border-white/15 bg-white/5 backdrop-blur-md text-foreground-100 font-label font-medium text-sm hover:border-primary-400/40 hover:bg-white/10 transition-all duration-500 whitespace-nowrap cursor-pointer"
                      >
                        <span>{t("shared.ctaSecondary")}</span>
                        <span className="flex items-center justify-center w-8 h-8 rounded-full border border-primary-400/40 text-primary-300 group-hover:bg-primary-500 group-hover:text-white group-hover:border-primary-500 transition-all duration-300">
                          <i className="ri-chat-smile-3-line text-sm" />
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Detail trigger */}
                  <button
                    type="button"
                    onClick={() => openDetail(current)}
                    className="mt-6 inline-flex items-center gap-2 text-xs font-label font-medium text-primary-300 hover:text-primary-200 transition-colors duration-300 cursor-pointer"
                  >
                    <i className="ri-add-circle-line text-base" />
                    {t("cs.centro.detail")}
                  </button>
                </div>
              </div>

              {/* Right: numbered index (desktop) */}
              <div className="hidden lg:block lg:col-span-5 lg:pl-8">
                <div className="border-l border-white/10">
                  {centroCards.map((c, idx) => {
                    const active = idx === current;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => openDetail(idx)}
                        className="group relative flex w-full items-center gap-4 py-3.5 pl-5 pr-3 text-left cursor-pointer"
                      >
                        <span
                          className={`absolute left-0 top-0 h-full w-[2px] transition-all duration-500 ${
                            active ? "bg-primary-400" : "bg-transparent"
                          }`}
                        />
                        <span
                          className={`font-heading text-xs w-6 transition-colors duration-300 ${
                            active ? "text-primary-300" : "text-white/30 group-hover:text-white/50"
                          }`}
                        >
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <span
                          className={`flex-1 font-label text-sm transition-colors duration-300 ${
                            active ? "text-white" : "text-white/45 group-hover:text-white/70"
                          }`}
                        >
                          {c.title}
                        </span>
                        <i
                          className={`ri-arrow-right-line text-sm transition-all duration-300 ${
                            active ? "text-primary-300 opacity-100" : "text-white/20 opacity-0 group-hover:opacity-100"
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Mobile progress dots */}
            <div className="mt-9 flex items-center gap-2 lg:hidden">
              {centroCards.map((c, idx) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => handleDotClick(idx)}
                  className={`h-1.5 flex-1 rounded-full transition-all duration-500 cursor-pointer ${
                    idx === current ? "bg-primary-400" : "bg-white/15 hover:bg-white/30"
                  }`}
                  aria-label={c.title}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Navigation arrows */}
        <button
          type="button"
          onClick={() => handleNavClick("prev")}
          className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-950/60 backdrop-blur-md border border-white/10 text-foreground-100/60 hover:text-white hover:border-primary-400/30 hover:bg-background-950/85 transition-all duration-300 cursor-pointer"
          aria-label="Anterior"
        >
          <i className="ri-arrow-left-line text-lg" />
        </button>
        <button
          type="button"
          onClick={() => handleNavClick("next")}
          className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-950/60 backdrop-blur-md border border-white/10 text-foreground-100/60 hover:text-white hover:border-primary-400/30 hover:bg-background-950/85 transition-all duration-300 cursor-pointer"
          aria-label="Siguiente"
        >
          <i className="ri-arrow-right-line text-lg" />
        </button>
      </section>

      {/* Detail modal */}
      {detailCard && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <div
            className="absolute inset-0 bg-background-950/85 backdrop-blur-sm animate-fade-in"
            onClick={closeDetail}
            aria-hidden="true"
          />
          <div className="relative w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-2xl border border-white/10 bg-background-900 animate-fade-in-up">
            {/* Header image */}
            <div className="relative h-40 sm:h-52 overflow-hidden">
              <img
                src={detailCard.image}
                alt={detailCard.title}
                width={1600}
                height={900}
                className="w-full h-full object-cover object-center"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-900 via-background-900/50 to-transparent" />
              <button
                type="button"
                onClick={closeDetail}
                aria-label={t("cs.centro.modal.close")}
                className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-background-950/60 backdrop-blur-md border border-white/15 text-white/80 hover:text-white hover:border-white/35 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>

              <div className="absolute bottom-4 left-5 right-5">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary-400/25 bg-primary-500/15 backdrop-blur-md mb-3">
                  <i className={`${detailCard.icon} text-primary-300 text-sm`} />
                  <span className="text-[10px] font-label font-semibold text-primary-200 uppercase tracking-[0.2em]">
                    {String((detailIndex ?? 0) + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-heading font-bold text-xl sm:text-2xl text-white leading-tight">
                  {detailCard.title}
                </h3>
              </div>
            </div>

            {/* Body */}
            <div className="p-5 sm:p-6">
              <p className="text-sm text-foreground-300/80 leading-relaxed mb-6">
                {detailCard.longDesc}
              </p>

              <p className="text-[10px] font-label font-semibold text-primary-300 uppercase tracking-[0.2em] mb-3">
                {t("cs.centro.modal.features")}
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-6">
                {detailCard.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-foreground-200/80 leading-snug">
                    <i className="ri-checkbox-circle-line text-primary-400 text-base mt-[1px] flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="flex items-center gap-6 mb-6 pt-5 border-t border-white/10">
                <div>
                  <p className="font-heading font-bold text-2xl text-primary-300 leading-none">{detailCard.stat}</p>
                  <p className="text-[10px] font-label text-foreground-400/50 uppercase tracking-wider mt-1">
                    {detailCard.statLabel}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    closeDetail();
                    scrollTo("#features");
                  }}
                  className="group inline-flex items-center gap-3 pl-5 pr-2 py-1.5 rounded-full border border-primary-400/50 bg-primary-500/15 text-white font-label font-medium text-sm hover:border-primary-400/80 hover:bg-primary-500/25 transition-all duration-500 whitespace-nowrap cursor-pointer"
                >
                  <span>{t("cs.centro.modal.ctaPrimary")}</span>
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white group-hover:bg-primary-400 transition-colors duration-300">
                    <i className="ri-arrow-right-line text-sm group-hover:translate-x-0.5 transition-transform duration-300" />
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    closeDetail();
                    onOpenQuote?.(detailCard.title);
                  }}
                  className="group inline-flex items-center gap-3 pl-5 pr-2 py-1.5 rounded-full border border-white/15 bg-white/5 text-foreground-100 font-label font-medium text-sm hover:border-primary-400/40 hover:bg-white/10 transition-all duration-500 whitespace-nowrap cursor-pointer"
                >
                  <span>{t("cs.centro.modal.ctaSecondary")}</span>
                  <span className="flex items-center justify-center w-8 h-8 rounded-full border border-primary-400/40 text-primary-300 group-hover:bg-primary-500 group-hover:text-white group-hover:border-primary-500 transition-all duration-300">
                    <i className="ri-chat-smile-3-line text-sm" />
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}